import { FolderDropdown, FolderItem, FolderList } from '@trucktrax/trucktrax-common';
import { ProductLine, RegionDto } from '@trucktrax/trucktrax-ts-common';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { AGGREGATE_STRING, CEMENT_STRING, READYMIX_VALUE } from '../../../constants/appConstants';
import { refreshGeoTraxState } from '../../../services/appService';
import { setReduxDriverList } from '../../../services/driversService';
import { getMapview } from '../../../services/mapviewsService';
import { getInboxMessages } from '../../../services/messagesService';
import { fetchPlantList } from '../../../services/plantsService';
import { fetchCurrentTicketList, selectTruckMarker } from '../../../services/ticketsService';
import { ReduxState } from '../../../store';
import { MarkerSelectedFrom } from '../../../store/actions/mapviewActions';
import { updateToFieldValue } from '../../../store/actions/messageActions';
import { clearSelectedTicket, resetTicketView } from '../../../store/actions/ticketActions';
import {
  OpenMapMarker,
  sortedOpenMarkersMatchingMapFilter,
} from '../../../store/selectors/mapViewSelectors';
import { MapMarker } from '../../../types';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import { determineMapMarkerId } from '../../../util/markerUtil';
import {
  Folder,
  FOLDER_LIST_SOURCE_KEY,
  FolderListSource,
  mapAggTrucksForFolderList,
  mapCementTrucksForFolderList,
  mapReadyMixTrucksForFolderList,
  retrieveProductLineNameDiff,
} from '../../../util/statusesUtil';
import styles from './MapPanelContent.module.css';
import ProductLineAndRegionManager from './ProductLineAndRegionManager';
import {
  TruckDesignations,
  selectMapMarkerFolderDesignations,
} from '../../../store/selectors/markerSelectors';

const TRUCKS_DROPDOWN_LABEL = 'Trucks';
const EXTERNAL_TRUCKS_DROPDOWN_LABEL = 'External Trucks';

type FolderDrowndownConfig = {
  name: string;
  data: MapMarker[];
  testInfo: { name: string; count: string };
  onClickDropdownHandler: (
    source: FolderListSource,
    selectedItemId: string,
    folderList: Folder<MapMarker>[],
    statusNameMap: { [name: string]: string | undefined },
  ) => void;
  onFolderClick: (stateMap: OpenStateMap) => void;
  onFolderItemClick: (item: FolderItem) => void;
  isDropdownOpen: boolean;
};
export class MapPanelContent extends Component<MapPanelContentProps> {
  static defaultProps: Partial<MapPanelContentProps> = {
    selectedMarker: undefined,
    selectedProductLine: ProductLine.None,
    isTruckTraxGeoEnabled: false,
  };

  state = {};

  onItemClick = (item: FolderItem) =>
    this.props.selectTruckMarker(
      {
        ...item.data,
        selectedFrom: MarkerSelectedFrom.TruckFolderList,
      },
      item.data.ticketUrl,
    );

  renderExternalTruckStatusFolders = () => {
    if (!this.props.isTruckTraxGeoEnabled || !this.props.showExternalTrucksFolder) {
      return null;
    }

    return this.renderFolderDropdown(FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS);
  };

  renderFolderDropdown = (folderSource: FolderListSource) => {
    const {
      data,
      name,
      testInfo,
      onClickDropdownHandler,
      onFolderClick,
      onFolderItemClick,
      isDropdownOpen,
    } = this.determineFolderSourceConfig(folderSource);
    const { length: truckCount } = data;
    const folders = this.generateFolderList(data, folderSource);
    const selectedItemId = determineMapMarkerId(this.props.openMarkers.at(0));
    return (
      <div className={styles.mapPanelLowerContent}>
        <FolderDropdown
          dataTestName={testInfo.name}
          dataTestCount={testInfo.count}
          classes={styles.folder}
          isOpen={isDropdownOpen}
          name={name}
          count={truckCount}
          onClick={this.handleFolderDropdownClick(folderSource, folders, onClickDropdownHandler)}
        >
          <FolderList
            folderList={folders}
            onFolderClick={onFolderClick}
            onItemClick={onFolderItemClick}
            selectedItemId={selectedItemId}
            isDarkTheme
            openStateMap={this.props.openStateMap}
          />
        </FolderDropdown>
      </div>
    );
  };

  handleFolderDropdownClick =
    (
      source: FolderListSource,
      folders: Folder<MapMarker>[],
      dropdownSourceHandler: FolderDrowndownConfig['onClickDropdownHandler'],
    ) =>
    (): void => {
      const selectedItemId = this.props.selectedMarker && this.props.selectedMarker.markerId;
      const statusNameMap = retrieveProductLineNameDiff(this.props.selectedProductLine);
      dropdownSourceHandler(source, selectedItemId ?? '', folders, statusNameMap);
    };

  determineFolderSourceConfig = (source: string): FolderDrowndownConfig => {
    const config: FolderDrowndownConfig = {
      name: '',
      data: [],
      testInfo: {
        name: `leftnav-trucks-dropdown-menu-${source}`,
        count: `leftnav-trucks-counter-${source}`,
      },
      onClickDropdownHandler: (
        _source: string,
        _selectedItemId: string,
        _folderList: Folder<MapMarker>[],
        _statusNameMap: { [name: string]: string | undefined },
      ) => {},
      onFolderClick: (_stateMap: OpenStateMap) => {},
      onFolderItemClick: (_item: FolderItem) => {},
      isDropdownOpen: false,
    };
    const {
      truckDesignations: { internal, external },
    } = this.props;
    if (source === FOLDER_LIST_SOURCE_KEY.INTERNAL_TRUCKS) {
      config.data = [...internal];
      config.name = TRUCKS_DROPDOWN_LABEL;
      config.isDropdownOpen = this.props.trucksDropdownIsOpen;
    } else if (source === FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS) {
      config.data = [...external];
      config.name = EXTERNAL_TRUCKS_DROPDOWN_LABEL;
      config.isDropdownOpen = this.props.externalTrucksDropdownIsOpen;
    }

    config.onClickDropdownHandler = this.handleDropdownClick;
    config.onFolderClick = this.props.setOpenStateMap;
    config.onFolderItemClick = this.onItemClick;

    return config;
  };

  generateFolderList = (
    truckMarkers: MapMarker[],
    folderSourceKey: FolderListSource,
  ): Folder<MapMarker>[] => {
    let folderList: Folder<MapMarker>[] = [];
    switch (this.props.selectedProductLine) {
      case READYMIX_VALUE:
        folderList = mapReadyMixTrucksForFolderList<MapMarker>(truckMarkers, folderSourceKey);
        break;
      case CEMENT_STRING:
        folderList = mapCementTrucksForFolderList<MapMarker>(truckMarkers, folderSourceKey);
        break;
      case AGGREGATE_STRING:
        folderList = mapAggTrucksForFolderList<MapMarker>(truckMarkers, folderSourceKey);
        break;
      default:
        folderList = [];
    }
    return folderList;
  };

  handleDropdownClick = (
    source: FolderListSource,
    selectedItemId: string,
    folderList: Folder<MapMarker>[],
    statusNameMap: { [name: string]: string | undefined },
  ) => {
    if (source === FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS) {
      this.props.toggleExternalTrucksDropdownIsOpen();
    } else {
      this.props.toggleTrucksDropdownIsOpen();
    }
    if (selectedItemId) {
      this.expandTruckFolder(source, selectedItemId, folderList, statusNameMap);
    }
  };

  expandTruckFolder(
    sourceFolder: FolderListSource,
    selectedItemId: string,
    folderList: Folder<MapMarker>[],
    statusNameMap: { [name: string]: string | undefined },
  ) {
    try {
      const isExternal = sourceFolder === FOLDER_LIST_SOURCE_KEY.EXTERNAL_TRUCKS;
      const {
        truckDesignations: { external, internal },
      } = this.props;
      const source = isExternal === true ? external : internal;
      const truck = source.find(truck => truck.id === selectedItemId);
      if (!truck) {
        recordLog(
          LogLevel.Debug,
          `Can not expand truck folder to selected truck: ${selectedItemId}`,
        );
        return;
      }
      const trucksFolderName = truck.data.status;
      const selectedStatusIdx = MapPanelContent.determineSelectedFolder(
        folderList,
        truck.data.status!,
        statusNameMap,
      );
      this.updateOpenStateMap(trucksFolderName, selectedStatusIdx);
    } catch (ex) {
      recordLog(LogLevel.Warn, 'Encountered an issue expanding Truck Folder', { error: ex });
    }
  }

  static determineSelectedFolder = (
    folders: Folder<MapMarker>[],
    id: string,
    map: { [name: string]: string | undefined },
  ) => folders.findIndex(item => map[item.id] === map[id]);

  updateOpenStateMap = (matchingFolder: string | null, selectedFolder: number) => {
    const openStateMapCopy = { ...this.props.openStateMap };
    if (matchingFolder) {
      openStateMapCopy[selectedFolder] = true; // make copy and send copy instead of inplace replacement
    } else {
      openStateMapCopy[0] = true;
    }
    this.props.setOpenStateMap(openStateMapCopy);
  };

  static hasDownloadReportPermission = () => false;

  render() {
    return (
      <div className={styles.mapPanelContent}>
        <ProductLineAndRegionManager
          regions={this.props.regions}
          onRegionChanged={this.props.onRegionChange}
        />
        {MapPanelContent.hasDownloadReportPermission()}
        {this.renderFolderDropdown(FOLDER_LIST_SOURCE_KEY.INTERNAL_TRUCKS)}
        {this.renderExternalTruckStatusFolders()}
      </div>
    );
  }
}

type OpenStateMap = { [index: number]: boolean };
type Marker = {
  markerId: string;
  lat: number;
  lng: number;
  isActive: boolean;
  status: string;
};
interface MapPanelContentOwnProps {
  openMarkers: OpenMapMarker[];
}
export interface MapPanelContentProps
  extends MapPanelContentOwnProps, MapPanelContentReduxStateProps {
  trucksDropdownIsOpen: boolean;
  externalTrucksDropdownIsOpen: boolean;
  regions: RegionDto[];
  openStateMap: OpenStateMap;
  selectedMarker?: Marker;
  onRegionChange: (regionUrl: string) => void;
  selectTruckMarker: (data: any, url: string) => void;
  setOpenStateMap: (map: OpenStateMap) => void;
  toggleTrucksDropdownIsOpen: () => void;
  toggleExternalTrucksDropdownIsOpen: () => void;
  selectedProductLine: ProductLine;
  isTruckTraxGeoEnabled: boolean;
  showExternalTrucksFolder: boolean;
}

export interface MapPanelContentReduxStateProps {
  truckDesignations: TruckDesignations;
  selectedProductLine: ProductLine;
  openMarkers: ReturnType<typeof sortedOpenMarkersMatchingMapFilter>;
}

function mapStateToProps(state: ReduxState): MapPanelContentReduxStateProps {
  return {
    selectedProductLine: state.selectedProductLine,
    openMarkers: sortedOpenMarkersMatchingMapFilter(state),
    truckDesignations: selectMapMarkerFolderDesignations(state),
  };
}

export default connect<MapPanelContentReduxStateProps, any, any, any>(mapStateToProps, {
  selectTruckMarker,
  fetchPlantList,
  getMapview,
  fetchCurrentTicketList,
  setReduxDriverList,
  clearSelectedTicket,
  resetTicketView,
  getInboxMessages,
  updateToFieldValue,
  refreshGeoTraxState,
})(MapPanelContent);
